export const personaOptions = [
  { value: "amputee", label: "Yes, I am the amputee" },
  { value: "caregiver", label: "No, I am helping a loved one" },
];

export const kLevelBlueprintData: Record<
  string,
  {
    kLevel: string;
    kLabel: string;
    kDescription: string;
    componentImageUrl: string;
    coverageText: string;
    componentSummary: string;
  }
> = {
  k0: {
    kLevel: "K0",
    kLabel: "Non-Ambulatory Potential",
    kDescription:
      "Based on your self-reported daily goals, your profile does not currently indicate a safe candidacy for prosthetic ambulation. This means insurance typically covers mobility equipment such as wheelchairs and transfer aids instead of a definitive prosthesis, pending further clinical evaluation.",
    componentImageUrl:
      "https://storage.readdy-site.link/project_files/19197903-8fa0-426f-850c-2c75ede20788/a26b1319-e961-4365-968c-ba1b3453d770_Below-Knee3.jpg",
    coverageText:
      "Insurance typically covers wheelchairs, transfer aids, and preparatory equipment for K0 candidates while functional status is reassessed.",
    componentSummary:
      "No definitive prosthetic component recommended at this time — a physician and prosthetist will reassess candidacy after addressing the factors below.",
  },
  k1: {
    kLevel: "K1",
    kLabel: "Household Ambulator",
    kDescription:
      "Based on your self-reported daily goals, your profile indicates a K1 mobility potential. This means insurance typically approves and covers basic prosthetic products designed for supported transfers and limited household walking on level surfaces.",
    componentImageUrl:
      "https://storage.readdy-site.link/project_files/19197903-8fa0-426f-850c-2c75ede20788/a26b1319-e961-4365-968c-ba1b3453d770_Below-Knee3.jpg",
    coverageText:
      "Insurance typically covers preparatory prostheses, basic mechanical knees, and SACH or single-axis feet for K1 ambulators.",
    componentSummary:
      "Standard preparatory socket with mechanical knee joint and SACH foot — optimized for safe, supported in-home transfers.",
  },
  k2: {
    kLevel: "K2",
    kLabel: "Limited Community Ambulator",
    kDescription:
      "Based on your self-reported daily goals, your profile indicates a K2 mobility potential. This means insurance typically approves and covers prosthetic products designed for low-level environmental navigation, including basic multi-axial feet and durable socket systems.",
    componentImageUrl:
      "https://storage.readdy-site.link/project_files/19197903-8fa0-426f-850c-2c75ede20788/a26b1319-e961-4365-968c-ba1b3453d770_Below-Knee3.jpg",
    coverageText:
      "Insurance typically covers multi-axial dynamic feet, lightweight pylons, and total surface bearing sockets for K2 ambulators.",
    componentSummary:
      "Multi-axial dynamic foot with reinforced socket and lightweight pylon — built for curbs, single steps, and steady outdoor walking.",
  },
  k3: {
    kLevel: "K3",
    kLabel: "Community Ambulator",
    kDescription:
      "Based on your self-reported daily goals, your profile indicates a K3 mobility potential. This means insurance typically approves and covers advanced, dynamic prosthetic products designed for variable speeds and outdoor terrain.",
    componentImageUrl:
      "https://storage.readdy-site.link/project_files/19197903-8fa0-426f-850c-2c75ede20788/a26b1319-e961-4365-968c-ba1b3453d770_Below-Knee3.jpg",
    coverageText:
      "Insurance typically covers energy-storing dynamic feet, advanced microprocessor knees, and custom adjustable socket systems for K3 ambulators.",
    componentSummary:
      "Energy-storing carbon fiber foot with microprocessor knee and micro-adjustable socket — engineered for variable speeds and all-terrain mobility.",
  },
  k4: {
    kLevel: "K4",
    kLabel: "High-Function / Athletic Ambulator",
    kDescription:
      "Based on your self-reported daily goals, your profile indicates a K4 mobility potential. This means insurance typically approves and covers high-impact prosthetic products designed for athletic activity, heavy labor, and extreme durability demands.",
    componentImageUrl:
      "https://storage.readdy-site.link/project_files/19197903-8fa0-426f-850c-2c75ede20788/a26b1319-e961-4365-968c-ba1b3453d770_Below-Knee3.jpg",
    coverageText:
      "Insurance typically covers running-specific blades, heavy-duty hydraulic knees, and reinforced socket systems engineered for high-impact forces.",
    componentSummary:
      "Athletic-grade running blade with heavy-duty hydraulic knee and reinforced dynamic socket — built for impact, speed, and maximum durability.",
  },
};

export const contactMethodOptions = [
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
  { value: "voice", label: "Voice Call" },
];

export const insuranceProviders = [
  "Blue Cross Blue Shield",
  "UnitedHealthcare",
  "Aetna",
  "Cigna",
  "Humana",
  "Medicare",
  "Medicaid",
  "Tricare",
  "Kaiser Permanente",
  "Anthem",
  "Centene",
  "Molina Healthcare",
  "VA Health Care",
  "Oscar Health",
];

export const adPanelContent = {
  defaultPage1: {
    title: "Empowering Patient-First Care Overview",
    subtitle:
      "See how Quick Prosthetics puts you in control of your prosthetic journey — from the comfort of home.",
    imagePrompt:
      "warm bright modern living room scene amputee person smiling confidently looking at tablet screen with caregiver nearby soft natural lighting neutral cream beige tones editorial lifestyle photography feeling of hope empowerment clean composition",
  },
  coverageAnalysis: {
    title: "Insurance Coverage Deep Dive: What Your Plan Actually Covers",
    subtitle:
      "Understand deductibles, pre-authorization requirements, L-Code billing — and how we handle all of it so you don't have to.",
    imagePrompt:
      "professional setting with insurance documents and prosthetic component catalog organized on desk bright clean environment healthcare administration aesthetic warm tones organized paperwork professional medical billing context",
  },
  accountCreation: {
    title: "Your Secure Digital Health Profile: Protected, Portable, Yours",
    subtitle:
      "All your data is encrypted end-to-end and HIPAA-compliant. Only you control who sees what — and you can share it with any clinic, anytime.",
    imagePrompt:
      "modern smartphone displaying secure health profile dashboard with lock icon and medical data visualization clean minimalist UI design on screen hand holding phone in bright home environment soft natural lighting data privacy concept healthcare technology aesthetic",
  },
  verificationRelief: {
    title: "You Did It — What Happens Next: The Path from Intake to Walking",
    subtitle:
      "We have everything we need to connect your care team. Here is exactly what happens in the next 24-48 hours.",
    imagePrompt:
      "amputee person relaxing comfortably on modern sofa at home looking at smartphone with peaceful relieved expression soft afternoon sunlight streaming through windows warm neutral interior calm satisfied atmosphere editorial lifestyle photography genuine contentment",
  },
  myfitScan: {
    title: "Precision Scans from the Comfort of Your Couch",
    subtitle:
      "MyFit uses your phone camera to capture the exact 3D metrics your physician needs for a perfect socket fit. No clinic visit, no measuring tape, no guesswork.",
    imagePrompt:
      "person sitting comfortably on modern sofa at home using smartphone to photograph their leg in bright natural window light calm organized living room warm neutral cream tones soft genuine atmosphere editorial lifestyle photography healthcare at home concept clean composition peaceful environment",
  },
  howItWorks3D: {
    title: "How the 3D Mobile Scan Works",
    subtitle:
      "Once you submit your profile, we text you a secure link to capture a quick 3D scan with your phone — giving your care team the precise measurements they need before your first appointment.",
    imagePrompt:
      "smartphone held in hand displaying 3D scan interface of lower limb measurement application modern clean UI on screen bright clean environment neutral background technology meets healthcare aesthetic soft natural lighting editorial style clean composition",
  },
};

export const complianceFooterText =
  "Notice: This platform is an administrative optimization tool. All AI-generated functional assessments, financial forecasts, wholesale cost aggregations, advertising recommendations, 3D metric visualizations, and documentation templates are strictly informational. Final clinical specifications, billing submittals, prescriptions, and custom fabrication workflows require formal, independent review and explicit sign-off by a licensed physician and a Certified Prosthetist prior to patient care.";

export const telemedTimeSlots = [
  {
    id: "slot-1",
    date: "Today",
    time: "2:15 PM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
  {
    id: "slot-2",
    date: "Today",
    time: "4:00 PM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
  {
    id: "slot-3",
    date: "Tomorrow",
    time: "9:30 AM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
  {
    id: "slot-4",
    date: "Tomorrow",
    time: "11:00 AM",
    duration: "20 min",
    physician: "Dr. S. Reeves, MD",
    available: true,
  },
  {
    id: "slot-5",
    date: "Tomorrow",
    time: "1:45 PM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
  {
    id: "slot-6",
    date: "Thu, Jun 12",
    time: "10:15 AM",
    duration: "20 min",
    physician: "Dr. S. Reeves, MD",
    available: true,
  },
  {
    id: "slot-7",
    date: "Thu, Jun 12",
    time: "2:30 PM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
  {
    id: "slot-8",
    date: "Fri, Jun 13",
    time: "8:00 AM",
    duration: "20 min",
    physician: "Dr. A. Paliga, MD",
    available: true,
  },
];
